import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ClipboardCheck, ListTodo, MessageSquareText, QrCode, Receipt, Sparkles, Star, Sunrise, TrendingUp, UtensilsCrossed, Users, Wifi, ShoppingBasket } from 'lucide-react';
import useSWR from 'swr';
import { buttonVariants } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import { getTablesSummary, getLayoutSummary, getQueueSummary, type TablesSummary, type LayoutSummary, type QueueSummary } from '@/api/dashboardSummary';
import { listChecklists, type ChecklistDto } from '@/api/ops_tasks';
import { listCameras } from '@/api/cameras';
import { getReviewsSettings, type ReviewsSettingsDto } from '@/api/reviews';
import type { Paginated } from '@/api/types';
import { useZoneFeatures } from '@/hooks/useZoneFeatures';
import type { FeatureKey } from '@/api/features';

export interface TeaserDef {
  icon: React.ElementType;
  titleKey: string;
  descKey: string;
  ctaKey: string;
  ctaHref: string;
  nameKey: string;
}

export const PLUGIN_TEASERS: Record<string, TeaserDef> = {
  checklists: {
    icon: ListTodo,
    nameKey: 'management.plugin.checklists',
    titleKey: 'dashboard.teaser.checklists.title',
    descKey: 'dashboard.teaser.checklists.desc',
    ctaKey: 'dashboard.teaser.checklists.cta',
    ctaHref: '/management/checklists',
  },
  tables: {
    icon: UtensilsCrossed,
    nameKey: 'management.plugin.tables',
    titleKey: 'dashboard.teaser.tables.title',
    descKey: 'dashboard.teaser.tables.desc',
    ctaKey: 'dashboard.teaser.tables.cta',
    ctaHref: '/management/zoning',
  },
  layout: {
    icon: ShoppingBasket,
    nameKey: 'management.plugin.layout',
    titleKey: 'dashboard.teaser.layout.title',
    descKey: 'dashboard.teaser.layout.desc',
    ctaKey: 'dashboard.teaser.layout.cta',
    // Was /management/equipment but configuring layout monitoring
    // is mostly drawing shelf polygons in зонирование — equipment
    // is where you add the camera but the layout-feature setup
    // lives in the zoning tab.
    ctaHref: '/management/zoning',
  },
  queue: {
    icon: Users,
    nameKey: 'management.plugin.queue',
    titleKey: 'dashboard.teaser.queue.title',
    descKey: 'dashboard.teaser.queue.desc',
    ctaKey: 'dashboard.teaser.queue.cta',
    ctaHref: '/management/zoning',
  },
};

// M1.2 (Alexey 11.09): «Подключенные функции» — every function, its status and «Настроить».
// «Отзывы» стоят двумя строками: с карт и по QR — их включают порознь, и
// по QR может быть настроено, но ни одного отзыва (код не распечатали).
const PLUGIN_ORDER = ['tables', 'layout', 'queue', 'staff_arrival', 'speech', 'checklists', 'receipts', 'sales_plan', 'visits',
  'reviews_maps', 'reviews_qr', 'wifi'] as const;

/** Две строки «Отзывов» — обе ведут на ту же страницу настроек (AI37: «а настройки
 *  и статистика как сейчас одной страницой с картами»), но статус считают по-разному. */
const REVIEW_ROWS: Record<'reviews_maps' | 'reviews_qr', { icon: React.ElementType; nameKey: string; href: string }> = {
  reviews_maps: { icon: Star, nameKey: 'features.reviewsMaps.name', href: '/management/reviews' },
  reviews_qr: { icon: QrCode, nameKey: 'features.reviewsQr.name', href: '/management/reviews' },
};

// Functions from the /features/ registry (switched on per branch), with their page.
const REGISTRY_ROWS: Record<Exclude<FeatureKey, 'checklists' | 'reviews'>, { icon: React.ElementType; nameKey: string; href: string }> = {
  speech: { icon: MessageSquareText, nameKey: 'features.speech.name', href: '/management/speech' },
  receipts: { icon: Receipt, nameKey: 'features.receipts.name', href: '/management/receipts' },
  sales_plan: { icon: TrendingUp, nameKey: 'features.sales_plan.name', href: '/management/sales-plan' },
  visits: { icon: ClipboardCheck, nameKey: 'features.visits.name', href: '/management/visits' },
  // Гостевой Wi-Fi включается на роутере, своей вкладки настроек у него нет (Alexey 16.09).
  wifi: { icon: Wifi, nameKey: 'features.wifi.name', href: '/management/equipment' },
  // «Открытие смены» (#1324): настройка — в «Очереди и сотрудники» (#1798).
  staff_arrival: { icon: Sunrise, nameKey: 'features.staff_arrival.name', href: '/management/queues-staff' },
};

export function PluginTeaserRow({
  def,
  search,
  canManage,
}: {
  def: TeaserDef;
  search: string;
  canManage: boolean;
}) {
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);
  const Icon = def.icon;

  return (
    <div className="rounded-md border border-dashed border-border bg-card">
      <button
        type="button"
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left"
        onClick={() => setExpanded(prev => !prev)}
      >
        <Icon className="size-4 shrink-0 text-muted-foreground" />
        <span className="flex-1 text-sm text-muted-foreground">{t(def.titleKey)}</span>
        {canManage && (
          <span className="text-xs font-medium text-primary">{t('dashboard.teaser.configure')}</span>
        )}
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform duration-200',
            expanded && 'rotate-180',
          )}
        />
      </button>
      {expanded && (
        <div className="space-y-3 border-t border-dashed border-border px-4 py-3">
          <p className="text-xs text-muted-foreground">{t(def.descKey)}</p>
          {canManage && (
            <Link
              to={{ pathname: def.ctaHref, search }}
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'text-xs')}
            >
              {t(def.ctaKey)}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export function WelcomeCard({ search, canManage }: { search: string; canManage: boolean }) {
  const { t } = useT();
  return (
    <div className="flex flex-col items-center gap-4 rounded-xl border border-dashed border-border py-12 text-center">
      <Sparkles className="size-10 text-muted-foreground" />
      <div className="space-y-1">
        <p className="text-base font-semibold">{t('dashboard.welcome.title')}</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          {canManage ? t('dashboard.welcome.subtitleAdmin') : t('dashboard.welcome.subtitleWorker')}
        </p>
      </div>
      {canManage && (
        <Link
          to={{ pathname: '/management/general', search }}
          className={cn(buttonVariants({ variant: 'default' }))}
        >
          {t('dashboard.welcome.cta')}
        </Link>
      )}
    </div>
  );
}

export function PluginSetupPanel({ zoneId }: { zoneId: string | null }) {
  const { t } = useT();
  const { search } = useLocation();

  const { data: tablesData, isLoading: tablesLoading } = useSWR<TablesSummary>(
    // Same SWR key as the dashboard's tables card → must use the SAME fetcher
    // (limit:0) so dedup doesn't serve one a capped result and the other all.
    // This card only reads `total`, so the limit is immaterial to it.
    zoneId ? ['dashboard:tables', zoneId] : null,
    async () => (await getTablesSummary(zoneId!, { limit: 0 })).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const { data: layoutData, isLoading: layoutLoading } = useSWR<LayoutSummary>(
    zoneId ? ['dashboard:layout', zoneId] : null,
    async () => (await getLayoutSummary(zoneId!)).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const { data: queueData, isLoading: queueLoading } = useSWR<QueueSummary>(
    zoneId ? ['dashboard:queues', zoneId] : null,
    async () => (await getQueueSummary(zoneId!)).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  // page_size 100 (not 1) so we see EVERY checklist + its items — the status
  // below aggregates across them, and it shares the dashboard's SWR key, which
  // already fetches 100 (a mismatched fetcher on the same key would clash).
  const { data: checklistsData, isLoading: checklistsLoading } = useSWR<Paginated<ChecklistDto>>(
    zoneId ? ['dashboard:checklists-status', zoneId] : null,
    async () => (await listChecklists(zoneId!, 100)).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  // The monitoring sections (tables/layout/queue) need a camera before any
  // zoning can produce data — so "no cameras yet" is a distinct, more actionable
  // state than "not configured". page_size 1 is enough to answer "any camera?".
  const { data: camerasData, isLoading: camerasLoading } = useSWR<Paginated<{ id: string }>>(
    zoneId ? ['zone-cameras-any', zoneId] : null,
    async () => (await listCameras(1, zoneId!)).data as Paginated<{ id: string }>,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const noCameras = camerasData ? camerasData.results.length === 0 : undefined;
  // Тот же ключ SWR, что у страницы настроек отзывов, — второго запроса не будет.
  const { data: reviewsData, isLoading: reviewsLoading } = useSWR<ReviewsSettingsDto>(
    zoneId ? ['reviews-settings', zoneId] : null,
    async () => (await getReviewsSettings(zoneId!)).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  const EQUIPMENT_HREF = '/management/equipment';

  const { features } = useZoneFeatures(zoneId);

  // Чеклисты: off → «Выключено»; on but empty → grey «Не настроено» (M1: grey
  // until there is content); with content, "set up" alone isn't "active" — a
  // sheets-synced checklist with sync off is paused, not running.
  const checklistStatus: PluginStatus = (() => {
    if (features === undefined || checklistsLoading || checklistsData === undefined) return undefined;
    if (!features.checklists?.enabled) return { labelKey: 'features.status.off', tone: 'muted' };
    const lists = checklistsData.results;
    if (lists.length === 0) return { labelKey: 'management.plugins.notConfigured', tone: 'muted' };
    const totalItems = lists.reduce((n, c) => n + (c.items?.length ?? 0), 0);
    if (totalItems === 0) return { labelKey: 'management.plugins.noTasks', tone: 'muted' };
    const anyActive = lists.some(c => (c.items?.length ?? 0) > 0 && (c.sync_on || !c.sheet_url));
    return anyActive
      ? { labelKey: 'management.plugins.active', tone: 'green' }
      : { labelKey: 'management.plugins.paused', tone: 'amber' };
  })();

  const registryStatus = (key: FeatureKey): PluginStatus => {
    if (features === undefined) return undefined;
    const st = features[key];
    if (!st) return undefined;
    if (st.requirement === 'microphone') return { labelKey: 'features.status.needsMic', tone: 'muted', href: EQUIPMENT_HREF };
    if (st.requirement === 'router') return { labelKey: 'features.status.needsRouter', tone: 'muted', href: EQUIPMENT_HREF };
    if (st.requirement === 'staff_polygon') return { labelKey: 'features.status.needsStaffPolygon', tone: 'muted', href: '/management/zoning' };
    if (!st.enabled) return { labelKey: 'features.status.off', tone: 'muted' };
    // «Приход к открытию» включён без единого дня — это не «не настроен», а ждёт
    // первого открытия; «Не настроен» ввёл бы в заблуждение (Остоженка, 08.10).
    if (!st.has_content && key !== 'staff_arrival') return { labelKey: 'management.plugins.notConfigured', tone: 'muted' };
    return { labelKey: 'management.plugins.active', tone: 'green' };
  };

  // Monitoring section: no cameras → "Нет камер" (→ equipment); else configured
  // (has zoning/polygons) → green; else not configured.
  const monitoringStatus = (configured: boolean | undefined, loading: boolean): PluginStatus => {
    if (loading || configured === undefined || camerasLoading || noCameras === undefined) return undefined;
    if (noCameras) return { labelKey: 'management.plugins.noCameras', tone: 'muted', href: EQUIPMENT_HREF };
    if (configured) return { labelKey: 'management.plugins.configured', tone: 'green' };
    return { labelKey: 'management.plugins.notConfigured', tone: 'muted' };
  };

  /** Карты: включено + вставлена хоть одна ссылка. */
  const reviewsMapsStatus = ((): PluginStatus => {
    const st = features?.reviews;
    if (features === undefined || !st) return undefined;
    if (!st.enabled) return { labelKey: 'features.status.off', tone: 'muted' };
    if (reviewsLoading || reviewsData === undefined) return undefined;
    const any = Object.values(reviewsData.sources || {}).some((src) => !!src);
    return any
      ? { labelKey: 'management.plugins.active', tone: 'green' }
      : { labelKey: 'management.plugins.notConfigured', tone: 'muted' };
  })();

  /** QR: форма есть — но пока ни одного отзыва, значит код не распечатали (AI37). */
  const reviewsQrStatus = ((): PluginStatus => {
    const st = features?.reviews;
    if (features === undefined || !st) return undefined;
    if (!st.enabled) return { labelKey: 'features.status.off', tone: 'muted' };
    if (reviewsLoading || reviewsData === undefined) return undefined;
    if (!reviewsData.form) return { labelKey: 'management.plugins.notConfigured', tone: 'muted' };
    if (!reviewsData.qr_review_count) return { labelKey: 'features.reviewsQr.noneYet', tone: 'muted' };
    return { labelKey: 'management.plugins.active', tone: 'green' };
  })();

  const statusByKey: Record<string, PluginStatus> = {
    checklists: checklistStatus,
    tables: monitoringStatus(tablesData ? tablesData.total > 0 : undefined, tablesLoading),
    layout: monitoringStatus(layoutData ? layoutData.total > 0 : undefined, layoutLoading),
    queue: monitoringStatus(queueData ? queueData.configured : undefined, queueLoading),
    speech: registryStatus('speech'),
    visits: registryStatus('visits'),
    staff_arrival: registryStatus('staff_arrival'),
    reviews_maps: reviewsMapsStatus,
    reviews_qr: reviewsQrStatus,
    wifi: registryStatus('wifi'),
  };

  return (
    <section>
      <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
        {t('features.heading')}
      </div>
      <div className="divide-y divide-border rounded-lg border border-border bg-card">
        {PLUGIN_ORDER.map(key => {
          const isReviewRow = key === 'reviews_maps' || key === 'reviews_qr';
          const reg = isReviewRow
            ? REVIEW_ROWS[key as keyof typeof REVIEW_ROWS]
            : key in REGISTRY_ROWS ? REGISTRY_ROWS[key as keyof typeof REGISTRY_ROWS] : null;
          // A function this backend doesn't ship (plugin not installed) has no row.
          // Обе строки отзывов живут на одной фиче reviews — её и спрашиваем.
          const featureKey = (isReviewRow ? 'reviews' : key) as FeatureKey;
          if (reg && features && !features[featureKey]) return null;
          const def = reg
            ? { icon: reg.icon, nameKey: reg.nameKey, ctaHref: reg.href }
            : PLUGIN_TEASERS[key];
          const Icon = def.icon;
          const status = statusByKey[key];
          const href = status?.href ?? def.ctaHref;
          return (
            <div key={key} className="flex items-center gap-3 px-4 py-3">
              <Icon className="size-4 shrink-0 text-muted-foreground" />
              <span className="flex-1 text-sm">{t(def.nameKey)}</span>
              {status === undefined ? (
                <span className="text-xs text-muted-foreground">…</span>
              ) : (
                <StatusPill labelKey={status.labelKey} tone={status.tone} />
              )}
              <Link
                to={{ pathname: href, search }}
                className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'text-xs')}
              >
                {t('dashboard.teaser.configure')}
              </Link>
            </div>
          );
        })}
      </div>
    </section>
  );
}

type PluginStatus = { labelKey: string; tone: 'green' | 'amber' | 'muted'; href?: string } | undefined;

function StatusPill({ labelKey, tone }: { labelKey: string; tone: 'green' | 'amber' | 'muted' }) {
  const { t } = useT();
  const cls =
    tone === 'green'
      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      : tone === 'amber'
        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
        : 'bg-muted text-muted-foreground';
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', cls)}>{t(labelKey)}</span>
  );
}
